/**
 * Pure helpers for renewal preview amounts. Contract totals from the API are
 * spread across `duration` months; ARR is total divided by contract years.
 */

export interface PreviewAmountSlice {
  totalAmount: number;
  duration?: number;
}

export function annualizedPreviewAmount(slice: PreviewAmountSlice): number {
  const months = slice.duration ?? 12;
  const years = months / 12;
  if (years <= 0) {
    return slice.totalAmount;
  }
  return Math.round(slice.totalAmount / years);
}

export function annualizedAmountForSelectedContractYears(
  totalAmount: number,
  durationMonths: number | undefined,
  previewSlices: readonly PreviewAmountSlice[] | undefined,
  contractYears: number
): number {
  const targetMonths = contractYears * 12;
  if (previewSlices?.length) {
    const match = previewSlices.find((row) => row.duration === targetMonths);
    if (match) {
      return annualizedPreviewAmount(match);
    }
  }
  return annualizedPreviewAmount({ totalAmount, duration: durationMonths });
}

/**
 * Total savings shown for the 3-year lock column vs the 1-year renewal column.
 * When 1-year renewal is not offered, the 3-year column is the baseline — returns 0.
 */
export function renewalGridThreeYearLockSavings(
  rows: readonly { oneYear: number; threeYears: number }[],
  oneYearRenewalOffered: boolean
): number {
  if (!oneYearRenewalOffered) {
    return 0;
  }
  const sumOneYear = rows.reduce((acc, row) => acc + row.oneYear, 0);
  const sumThreeYear = rows.reduce((acc, row) => acc + row.threeYears, 0);
  return sumOneYear - sumThreeYear;
}

/**
 * Total savings for the 5-year lock vs the applicable baseline: 1-year renewal path
 * when offered, otherwise the 3-year renewal path (no 1-year column).
 */
export function renewalGridFiveYearLockSavings(
  rows: readonly {
    oneYear: number;
    threeYears: number;
    fiveYears: number;
  }[],
  oneYearRenewalOffered: boolean
): number {
  const sumFiveYear = rows.reduce((acc, row) => acc + row.fiveYears, 0);
  if (oneYearRenewalOffered) {
    const sumOneYear = rows.reduce((acc, row) => acc + row.oneYear, 0);
    return sumOneYear - sumFiveYear;
  }
  const sumThreeYear = rows.reduce((acc, row) => acc + row.threeYears, 0);
  return sumThreeYear - sumFiveYear;
}

/**
 * When 1-year renewal is not offered, year 5 cannot step on a 1-year cadence; keep
 * the 3-year lock column at the same annual amount as year 4.
 */
export function alignThreeYearLockYearFiveWithYearFour<
  T extends { threeYears: number }
>(rows: readonly T[], oneYearRenewalOffered: boolean): T[] {
  if (oneYearRenewalOffered || rows.length < 5) {
    return [...rows];
  }
  return rows.map((row, index) =>
    index === 4 ? { ...row, threeYears: rows[3].threeYears } : row
  );
}
